// src/guards/ProtectedRoute.tsx
// Client-side auth guard — redirects to /auth/login if the user is not
// authenticated, and to /pending-approval if the account is not yet approved.
// Uses Supabase session from the browser client.

import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { supabase } from '@/src/lib/supabase'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)
  const [approved, setApproved] = useState<boolean | null>(null)
  const location = useLocation()

  useEffect(() => {
    async function checkAuth() {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        setAuthenticated(false)
        setLoading(false)
        return
      }

      setAuthenticated(true)

      // Check approval status from users table
      const { data: userRow } = await supabase
        .from('users')
        .select('approved')
        .eq('id', session.user.id)
        .single()

      // Fail open if the query fails (bridge enforces approval on API calls)
      setApproved(userRow?.approved ?? true)
      setLoading(false)
    }

    checkAuth()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthenticated(!!session)
      if (!session) {
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
      </div>
    )
  }

  if (!authenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  if (approved === false) {
    return <Navigate to="/pending-approval" replace />
  }

  return <>{children}</>
}
